<template>
  <div class="product-search">
    <h4 class="size-16 fw-600 text-dark mb-3">Paso 2: Busca tu producto</h4>

    <!-- Producto seleccionado -->
    <div v-if="selectedProduct" class="bg-white rounder-8 border p-3 d-flex align-items-center gap-3 mb-3">
      <img
        :src="getImageSrc(selectedProduct.image)"
        @error="handleImageError"
        alt="producto"
        style="width: 56px; height: 56px; object-fit: contain;"
        class="rounder-8 border"
      />
      <div class="flex-grow-1">
        <div class="size-14 fw-600 text-dark">{{ selectedProduct.sku }}</div>
        <div class="size-13 text-secondary">{{ selectedProduct.long_name }}</div>
        <div v-if="selectedProduct.prop_extra_5" class="size-12 text-muted">Marca: {{ selectedProduct.prop_extra_5 }}</div>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-danger rounder-8"
        @click="$emit('clear')"
      >
        Quitar
      </button>
    </div>

    <!-- Buscador -->
    <div v-else>
      <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 mb-3">
        <div class="search-wrapper px-2 flex-grow-1">
          <select
            v-model="searchType"
            class="form-select fw-400"
          >
            <option value="codigo">SKU Exacto</option>
            <option value="codigo_similar">SKU Similar</option>
            <option value="nombre">Nombre</option>
          </select>
          <form @submit.prevent="handleSearch" class="search-form bg-white">
            <div class="search-container">
              <input
                type="text"
                class="form-control-search"
                v-model="searchQuery"
                :placeholder="placeholder"
                @keydown.enter.prevent="handleSearch"
              />
              <font-awesome-icon
                v-if="searchQuery !== ''"
                :icon="['fas', 'times']"
                class="clear-icon cursor-pointer"
                @click="clearSearch"
              />
            </div>
          </form>
        </div>
        <button
          type="button"
          class="btn bgc-dark text-white rounder-8 px-4 w-100 w-sm-auto"
          style="height: 44.8px;"
          @click="handleSearch"
          :disabled="loading || !searchQuery.trim()"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Buscar
        </button>
      </div>

      <!-- Resultados -->
      <div v-if="productResults.length > 0" class="bg-white rounder-8 border">
        <div class="p-2 border-bottom">
          <span class="size-12 text-muted">{{ productResults.length }} resultado(s) encontrado(s)</span>
        </div>
        <div
          v-for="(item, index) in productResults"
          :key="index"
          class="d-flex align-items-center gap-3 p-3 cursor-pointer product-result"
          :class="{ 'border-bottom': index < productResults.length - 1 }"
          @click="selectProduct(item)"
        >
          <img
            :src="getImageSrc(item.image)"
            @error="handleImageError"
            alt="producto"
            style="width: 48px; height: 48px; object-fit: contain;"
            class="rounder-8 border"
          />
          <div class="flex-grow-1">
            <div class="size-13 fw-600 text-dark">{{ item.sku }}</div>
            <div class="size-12 text-secondary">{{ item.long_name }}</div>
            <div v-if="item.prop_extra_5" class="size-11 text-muted">Marca: {{ item.prop_extra_5 }}</div>
          </div>
          <img src="/icons/arrow-right-positive.svg" alt="seleccionar" style="width: 16px; height: 16px;" />
        </div>
      </div>

      <!-- Sin resultados -->
      <div v-else-if="hasSearched && !loading" class="bg-white rounder-8 border p-3">
        <span class="size-13 text-secondary fst-italic">No se encontraron productos. Intenta con otro término.</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductSearch',
  props: {
    selectedProduct: {
      type: Object,
      default: null
    },
    productResults: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      searchType: 'codigo',
      hasSearched: false
    }
  },
  computed: {
    placeholder() {
      if (this.searchType === 'codigo') {
        return 'Buscar por SKU Exacto'
      } else if (this.searchType === 'codigo_similar') {
        return 'Buscar por SKU Similar'
      } else if (this.searchType === 'nombre') {
        return 'Buscar por nombre del producto'
      }

      return 'Buscar por nombre o SKU'
    }
  },
  methods: {
    handleSearch() {
      if (!this.searchQuery.trim()) return
      this.hasSearched = true
      this.$emit('search', {
        query: this.searchQuery.trim(),
        searchType: this.searchType
      })
    },
    clearSearch() {
      this.searchQuery = ''
      this.hasSearched = false
      this.$emit('clearResults')
    },
    selectProduct(product) {
      this.$emit('select', product)
      this.searchQuery = ''
      this.hasSearched = false
    }
  }
}
</script>

<style scoped>
.search-wrapper {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #CED4DA;
  border-radius: 8px;
  min-height: 44.8px;
  height: auto;
  flex: 1;
}

.search-form {
  background-color: #fff;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
}

.form-control-search {
  padding: 5px 5px !important;
  width: 90%;
  height: 100%;
  background-color: #fff !important;
  border: none !important;
  outline: none !important;
}

.form-select {
  padding: 5px 25px 5px 5px !important;
  width: 100px;
  height: 80%;
  background-color: #e9ecef;
  color: #ADB5BD !important;
  border: none !important;
  cursor: pointer;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23ADB5BD' d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px;
}

.form-select:focus {
  outline: none !important;
  box-shadow: none !important;
}

input::placeholder {
  color: #ADB5BD !important;
}

.search-container {
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  position: relative;
}

.clear-icon {
  color: #6c757d;
  font-size: 14px;
  margin-right: 8px;
  cursor: pointer;
}

.bgc-dark {
  background-color: #09090B !important;
}

.product-result {
  transition: background-color 0.15s ease;
}

.product-result:hover {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 575.98px) {
  .w-sm-auto {
    width: auto !important;
  }
}

@media (min-width: 576px) {
  .w-sm-auto {
    width: auto !important;
  }
}
</style>
